/**
 * Starter-pack panel — one-click seeder for the 5 starter templates per
 * service (Methodology + Artefact + Deliverable + Runbook + Sector overlay).
 *
 * Renders inside the Toolkits admin tab. Shows a per-service progress chip
 * (e.g. "3/5") plus a single "Seed" button that calls the backend; the
 * backend is idempotent so the button is safe to click twice.
 *
 * A separate "Seed every service" button at the top fires the all-services
 * endpoint for first-time setup.
 */
import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Mail, Package, RefreshCw, Send, Sparkles, FilePen } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function StarterPackPanel({ onSeeded }) {
  const [services, setServices] = useState([]);
  const [expected, setExpected] = useState(5);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(null);
  const [seedingAll, setSeedingAll] = useState(false);
  const [emailing, setEmailing] = useState(null);
  const [emailingAll, setEmailingAll] = useState(false);
  const [refreshingMd, setRefreshingMd] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/toolkits/starter-pack/services");
      setServices(data.items || []);
      setExpected(data.expected_per_service || 5);
    } catch {
      toast.error("Failed to load starter-pack status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const seedOne = async (slug) => {
    setSeeding(slug);
    try {
      const { data } = await api.post(`/admin/toolkits/starter-pack/${slug}`);
      const seeded = data.seeded_count || 0;
      const skipped = data.skipped_count || 0;
      toast.success(`${data.service_name}: ${seeded} added, ${skipped} already present`);
      await reload();
      onSeeded?.();
    } catch (e) {
      toast.error(`Seed failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSeeding(null);
    }
  };

  const seedAll = async () => {
    if (!window.confirm("Seed starter templates for every service that doesn't have them yet?")) return;
    setSeedingAll(true);
    try {
      const { data } = await api.post("/admin/toolkits/starter-pack/all");
      toast.success(`Seeded ${data.seeded_total} new template(s) across 12 services. ${data.skipped_total} already existed.`);
      await reload();
      onSeeded?.();
    } catch (e) {
      toast.error(`Seed failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSeedingAll(false);
    }
  };

  const emailOne = async (slug) => {
    setEmailing(slug);
    try {
      const { data } = await api.post(`/admin/toolkits/starter-pack/${slug}/email`);
      toast.success(`Emailed to ${data.recipient} (${data.file_count} files · ${data.size_kb} KB)`);
    } catch (e) {
      toast.error(`Email failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setEmailing(null);
    }
  };

  const emailAll = async () => {
    if (!window.confirm("Email a single ZIP containing every service's templates to your account?")) return;
    setEmailingAll(true);
    try {
      const { data } = await api.post("/admin/toolkits/starter-pack/email-all");
      toast.success(`Emailed to ${data.recipient} (${data.file_count} files · ${data.size_kb} KB)`);
    } catch (e) {
      toast.error(`Email failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setEmailingAll(false);
    }
  };

  const refreshMarkdown = async () => {
    if (!window.confirm(
      "Re-render the Methodology / Runbook / Sector-overlay markdown for every service " +
      "using the latest framework anchors and CSI moves?\n\n" +
      "Item IDs and version history are preserved. Binary items (.docx artefacts) are untouched."
    )) return;
    setRefreshingMd(true);
    try {
      const { data } = await api.post("/admin/toolkits/starter-pack/refresh-markdown");
      toast.success(
        `Refreshed ${data.refreshed_count} markdown item(s).` +
        (data.skipped_count ? ` Skipped ${data.skipped_count} (no matching item — seed first).` : "")
      );
      await reload();
      onSeeded?.();
    } catch (e) {
      toast.error(`Refresh failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRefreshingMd(false);
    }
  };

  const fullyCount = services.filter((s) => s.fully_seeded).length;

  return (
    <div className="border border-zinc-200 bg-white p-5 mb-6" data-testid="starter-pack-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-[#F4F2FB] flex items-center justify-center flex-shrink-0">
            <Package size={16} className="text-[#7737F6]" />
          </div>
          <div>
            <h3 className="text-[14px] font-semibold text-black">Starter templates</h3>
            <p className="text-zinc-500 text-[12.5px] mt-0.5 max-w-2xl">
              One-click seeder for the five default templates per service
              (Methodology · Artefact · Deliverable · Runbook · Sector overlay).
              Idempotent — re-clicking only adds what's missing.
              {" "}
              <span className="text-zinc-700">{fullyCount}/{services.length} services fully seeded.</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={reload}
            disabled={loading}
            className="text-zinc-500 hover:text-black text-[12px] flex items-center gap-1 disabled:opacity-50"
            data-testid="starter-pack-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            type="button"
            onClick={refreshMarkdown}
            disabled={refreshingMd}
            className="ek-btn ek-btn-outline text-[12.5px] disabled:opacity-50"
            data-testid="starter-pack-refresh-markdown"
            title="Re-render the Methodology / Runbook / Sector-overlay markdown using the latest framework anchors + CSI moves"
          >
            <FilePen size={12} />
            {refreshingMd ? "Refreshing…" : "Refresh markdown content"}
          </button>
          <button
            type="button"
            onClick={emailAll}
            disabled={emailingAll || fullyCount === 0}
            className="ek-btn ek-btn-outline text-[12.5px] disabled:opacity-50"
            data-testid="starter-pack-email-all"
            title="Email a single ZIP of every service's templates to your account"
          >
            <Send size={12} />
            {emailingAll ? "Sending…" : "Email me everything"}
          </button>
          <button
            type="button"
            onClick={seedAll}
            disabled={seedingAll || fullyCount === services.length}
            className="ek-btn ek-btn-primary text-[12.5px] disabled:opacity-50"
            data-testid="starter-pack-seed-all"
          >
            <Sparkles size={13} />
            {seedingAll ? "Seeding…" : fullyCount === services.length ? "Everything seeded" : "Seed every service"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {loading ? (
          <div className="text-zinc-400 text-[13px] py-2">Loading…</div>
        ) : services.map((s) => (
          <div
            key={s.slug}
            className="flex items-center justify-between gap-3 px-3 py-2 border border-zinc-200"
            data-testid={`starter-pack-row-${s.slug}`}
          >
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-medium text-black truncate" title={s.name}>{s.name}</div>
              <div className="text-[11px] text-zinc-500">{s.present_count} / {expected} templates</div>
            </div>
            {s.fully_seeded ? (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => emailOne(s.slug)}
                  disabled={emailing === s.slug}
                  className="text-zinc-500 hover:text-[#7737F6] disabled:opacity-50"
                  data-testid={`starter-pack-email-${s.slug}`}
                  title="Email this pack to me"
                >
                  {emailing === s.slug ? <RefreshCw size={12} className="animate-spin" /> : <Mail size={12} />}
                </button>
                <span className="inline-flex items-center gap-1 text-emerald-700 text-[10.5px] font-semibold tracking-[0.14em] uppercase">
                  <CheckCircle2 size={11} /> Seeded
                </span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => seedOne(s.slug)}
                disabled={seeding === s.slug}
                className="text-[11.5px] font-semibold border border-[#7737F6] text-[#7737F6] hover:bg-[#7737F6] hover:text-white transition-colors px-2.5 py-1 disabled:opacity-50"
                data-testid={`starter-pack-seed-${s.slug}`}
              >
                {seeding === s.slug ? "…" : "Seed"}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
